import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormControl } from '@angular/forms';
import { catchError, map, Observable, startWith, Subscription } from 'rxjs';
import { EntityChartUpdateService } from './entity-chart-update.service';
import { ChartCodeModel } from './chart-code-model';
import { DeleteTransactionModalComponent } from './delete-transaction-modal/delete-transaction-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import {
  generalConfigFromGlobal,
  hydratedGlobalParams,
  extractObject,
} from '../../shared/workspace-element-context';

@Component({
    selector: 'gtw-entity-chart-update',
    templateUrl: './entity-chart-update-mat.component.html',
    styleUrls: ['./entity-chart-update.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class EntityChartUpdateComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  baseGlobalParams: { jcd_key: string; tax_year: string; scenario: string; client_key: string; scenarioCode: string;} | undefined;

  private baseURL = '/custom';
  private baseURLGtw = '/gtw';
  private taxYear :number = 0;
  private scenario :number = 0;
  private jcdKey :number = 0;
  private ccList :string = "";//code combination
  private filingGroup :number = 0;
  private processName :string = 'UPDATE_ENTITY_CHART_PROCESS';
  private checkLockedbj: any = [];// locking object;
  private contextFingerprint = '';

  @Output('cancel-chart-update')
  cancelChartUpdate = new EventEmitter<string>();

  disableEntity = true;
  
  allEntityDetails:any =[];
  allEntityDetails_backup:any =[];

  isLoading = false;
  isLoadingDomestic = false;
  isLoadingForeign = false;
  subscriptions:Subscription = new Subscription() ;
  
  @ViewChild('baseModalCustom', { static: false }) baseModalCustom: any;

  allEntitiesChartCodeFormControls:Array<UntypedFormControl> = []; //new FormControl();

  optionsDomestic:Array<ChartCodeModel> = [];
  optionsForeign:Array<ChartCodeModel> = [];
  optionsTransCount:Array<any>=[];
  
  allFilteredOptions: Array<Observable<ChartCodeModel[]>> = [];

  focusEntity !:any;
  focusEntityI!:number;
  focusEntityJ!:number;
  updateEntityList:Array<any> = [];
  delayConfirm = false;

  type  = {delete: 'delete', save: 'save'};
  onChartChangeHeader = 'Delete Transaction(s)?';
  onChartChangeMessage = 'To update chart, existing transaction(s) should be removed. Please confirm deletion of transaction(s)?';
  onSaveHeader = 'Confirm';
  onSave = 'Chart will be updated and transaction(s) will be deleted, do you want to continue?';
  deleteLabel = 'Transaction will be deleted.';
  
  disableDelete = false;
  backupEntityObject: any ={};

  modalName = 'show-custom-modal';

  constructor(
    private entityChartUpdateService: EntityChartUpdateService,
    public dialog: MatDialog,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService
  ) { }
  
  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subscriptions.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.subscriptions.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.init();
      })
    );
    this.init();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.baseURL = gc.custom || '/custom';
    this.baseURLGtw = gc.api || '/gtw';

    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace);
    const gp = extractObject(this.gtwGlobal.global?.globalParams || {});
    const scenarioCode =
      gp.scenarioCode ||
      gp.scenario_code ||
      (hydrated as any)?.scenarioCode ||
      (hydrated as any)?.scenario_code ||
      '';
    const isIssue =
      gp.is_issue_key_enabled ??
      gp.isIssueKeyEnabled ??
      (hydrated as any)?.is_issue_key_enabled ??
      '';

    this.baseGlobalParams = {
      ...extractObject(hydrated),
      ...gp,
      scenarioCode,
      is_issue_key_enabled: isIssue,
    } as any;

    this.taxYear = parseInt(String(gp.tax_year ?? '0'), 10) || 0;
    this.scenario = parseInt(String(gp.scenario ?? '0'), 10) || 0;
    this.jcdKey = parseInt(String(gp.jcd_key ?? '0'), 10) || 0;

    const d = activeScreen?.data || {};
    this.ccList = String(d.ccList ?? d.cc_list ?? '');
    this.filingGroup = parseInt(String(d.filingGroup ?? d.filing_group ?? '0'), 10) || 0;
    if (d.processName != null || d.process_name != null) {
      this.processName = String(d.processName ?? d.process_name);
    } else if (!this.processName) {
      this.processName = 'UPDATE_ENTITY_CHART_PROCESS';
    }
    this.checkLockedbj = d.checkLockedBj ?? d.check_locked_bj ?? [];

    const fp = `${this.taxYear}|${this.scenario}|${this.jcdKey}|${this.filingGroup}|${this.ccList}|${this.baseURL}|${this.baseURLGtw}`;
    if (fp !== this.contextFingerprint) {
      this.contextFingerprint = fp;
      this.allEntityDetails = [];
      this.allEntityDetails_backup = [];
      this.optionsDomestic = [];
      this.optionsForeign = [];
      this.optionsTransCount = [];
      this.allFilteredOptions = [];
      this.allEntitiesChartCodeFormControls = [];
    }
  }

  init(){
    if(this.checkInputType()){
      
      this.getAllEntityDetails();
      this.getAllChartCodes("D");
      this.getAllChartCodes("F");
      this.getAllTransCount();

    }
  }

  checkInputType():boolean{
    if(Number.isInteger(parseInt(this.taxYear+"")) && 
       Number.isInteger(parseInt(this.scenario+'')) && 
       Number.isInteger(parseInt(this.jcdKey +'')) && 
       Number.isInteger(parseInt(this.filingGroup+''))){
      return true;
    }

    return false;
  }

  getAllEntityDetails (){

    if((this.allEntityDetails == null || this.allEntityDetails.length == 0) && !this.isLoading) {
      this.isLoading = true;
      let getAllSubscription = this.entityChartUpdateService.getAllEntityChartList(this.baseURL,this.taxYear,this.scenario,this.jcdKey,this.ccList,this.filingGroup)
      .subscribe(
        (entities:any) => {
          this.isLoading = false;
          this.allEntityDetails = entities;
          this.allEntityDetails_backup = JSON.parse(JSON.stringify(entities));
          this.setupDistinctEntities();
        },
        error => {
          console.error(error);
          this.isLoading = false;
        },
        () => {
          this.isLoading = false;
          console.log("Completed all entities");
        }
        );
      this.subscriptions.add(getAllSubscription);
    }
  }

  getAllTransCount(){
    this.entityChartUpdateService
      .getTransCount(this.baseURLGtw,this.taxYear,this.scenario,this.jcdKey,this.ccList).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        (res:any)=>{console.log(res)
          this.optionsTransCount = res;
        },
        (err)=>console.log(err)
      );
  }
  
  setupDistinctEntities(){
    this.allEntityDetails.map((entity:any, i:number)=>{
      entity.selection =false;
      this.setFilteredOption(entity,i);
    });
  }

  getAllChartCodes(type:string) {
    
    if((this.optionsDomestic.length == 0 && type == 'D' && !this.isLoadingDomestic) || (this.optionsForeign.length == 0 && type == 'F' && !this.isLoadingForeign)) {
      if(type == 'D'){
        this.isLoadingDomestic = true;
      } else {
        this.isLoadingForeign = true;
      }
      let getChartCodeSubscription = this.entityChartUpdateService.getChartCodes(this.baseURL,this.taxYear,this.scenario,this.jcdKey,type)
        .subscribe(
          (codes:Array<ChartCodeModel>) => {
            console.log(codes);
            if(type == 'D'){
              this.optionsDomestic = codes;
              this.isLoadingDomestic = false;
            }else{
              this.optionsForeign = codes;
              this.isLoadingForeign = false;
            }
          },
          (error)=>console.error(error),
          () => console.log("Completed chart code")
        )
      this.subscriptions.add(getChartCodeSubscription);
    }
  }

  private _filter(value: any,type:string):ChartCodeModel[]{

    if(value && typeof value != "object"){
      const filterValue = value?.toLowerCase();
      if(type== 'D'){
        return this.optionsDomestic.filter((option:ChartCodeModel) => option.code.toLowerCase().includes(filterValue));
      }
      return this.optionsForeign.filter((option:ChartCodeModel) => option.code.toLowerCase().includes(filterValue));
    }
    return [];
  }

  backupEntity(entity:any,i:number,j:number){
    if(i!=this.focusEntityI || j != this.focusEntityJ || entity['source_SYSTEM_FLAG'] != this.backupEntityObject['source_SYSTEM_FLAG']){
      this.focusEntity = entity;
      this.focusEntityI = i;
      this.focusEntityJ = j;
      this.backupEntityObject = JSON.parse(JSON.stringify(this.allEntityDetails_backup[i]));
    }
  }

  selectedOption(chartCode:any){
    console.log(chartCode.value);
    this.focusEntity['chart_CODE'] = chartCode.value['code']  ;
    this.focusEntity['chart_KEY'] = chartCode.value['key'] ;
    this.confirmDelete('show-custom-modal',this.focusEntity,this.focusEntityI,this.focusEntityJ);
  }

  displayFn(option:ChartCodeModel): string {
    return (option)? option.code : '';
  }

  checkboxClicked(entity:any,i:number,j:number) {
    this.backupEntity(entity,i,j);
    this.openDialog(this.onChartChangeHeader,this.onChartChangeMessage, this.type.delete);
  }

  clearButtonClicked(event:Event,entity:any,i:number,j:number) {
    this.backupEntity(entity,i,j);
    this.cancelUpdate(this.modalName);
    event?.stopPropagation();
    
  }

  confirmDelete(id: string, entity:any,i:number,j:number) {
    
    if(parseInt(entity.groupItems[j].trans_COUNT) <= 0){
      this.proceedWithUpdate(id);
    } else {
      if(entity.chart_CODE != this.backupEntityObject?.chart_CODE){
        this.baseModalCustom?.showMore(id);
        this.openDialog(this.onChartChangeHeader,this.onChartChangeMessage, this.type.delete);
      }
    }    
  }

  cancelUpdate(id: string) {
      this.baseModalCustom?.closeModal(id);
      this.focusEntity['chart_CODE'] = this.backupEntityObject['chart_CODE'];
      this.focusEntity['chart_KEY'] = this.backupEntityObject['chart_KEY'];
      this.focusEntity['groupItems'][this.focusEntityJ]['selection']= false;  
      this.setFilteredOption(this.focusEntity,this.focusEntityI);
  }

  proceedWithUpdate(id: string) {
    this.focusEntity['groupItems'][this.focusEntityJ]['selection'] = true;
    this.setFilteredOption(this.focusEntity,this.focusEntityI);
    this.baseModalCustom?.closeModalOk(id);
  }

  private setFilteredOption(entity:any,i:number){
    if(entity && entity['source_SYSTEM_FLAG'] == 'D'){
      let fc = new UntypedFormControl({key:entity.chart_KEY,code:entity.chart_CODE});
      this.allFilteredOptions[i] = fc.valueChanges
          .pipe(
            startWith(''),
            map((value:string) => this._filter(value,'D'))
          );
      this.allEntitiesChartCodeFormControls[i] =fc; 
    } else {
      let fc = new UntypedFormControl({key:entity.chart_KEY,code:entity.chart_CODE});
      this.allFilteredOptions[i]= fc.valueChanges
          .pipe(
            startWith(''),
            map((value:string) => this._filter(value,'F'))
          );
      this.allEntitiesChartCodeFormControls[i]=fc; 
    }
  }

  updateEntityChart(){
    this.isLoading =true;
    var settings = {
      "tax_year": this.taxYear,
      "scenario": this.scenario,
      "jcd_key": this.jcdKey
    };
    this.updateEntityList = this.allEntityDetails.filter((entity:any) => !!entity['groupItems'][0]['selection']);
    if(!this.updateEntityList || this.updateEntityList.length < 1){
      this.isLoading =false;
      this.entityChartUpdateService.openSnackBar('No TB chart selected to update', 'gtw-snackbar--yellow');
      return;
    }
    let updateSubscription = this.entityChartUpdateService.updateEntityChartCodes(this.baseURLGtw,this.taxYear,this.processName,settings,this.updateEntityList,this.baseGlobalParams,this.checkLockedbj).subscribe(
      (response)=>{
            this.isLoading =false;
            if(response.callSuccess == "1") {
              this.entityChartUpdateService.openSnackBar('Entity Chart updated','gtw-snackbar--green');
              this.cancelChartUpdate.emit('refresh-chart-update');
            } else {
              this.entityChartUpdateService.openSnackBar('Entity Chart update failed. '+response.errorMessage, 'gtw-snackbar--red');
            }
      },
      error => { this.isLoading =false;this.entityChartUpdateService.openSnackBar('Entity Chart update failed. '+error.error.errorMessage, 'gtw-snackbar--red'); },
      () => { console.log("Update Completed");this.isLoading =false; }
    );
    this.subscriptions.add(updateSubscription);
  }

  // this is for google material component

  openDialog(header:string = this.onChartChangeHeader, msg:string = this.onChartChangeMessage, type: string = 'delete') {
    const data = {
      data: {
        message : msg,
        header: header,
        type: type
      }
    };
    const dialogRef = this.dialog.open(DeleteTransactionModalComponent,data);

    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result:any) => {
      console.log(`Dialog result: ${result}`);
      if(result == 'delete'){
        this.proceedWithUpdate(this.modalName);
      }else if(result == 'save'){
        this.updateEntityChart();
      }else {
        this.cancelUpdate(this.modalName);
      }
      
    });
  }

  // appkit html
  modalDetermine() {
    console.log("click ok");
  }

  modalClose() {
      console.log("click close");
      this.cancelChartUpdate.emit('cancel-chart-update');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    console.log('Destroyed');
  }

}
